// Immersive portfolio page — monolithic iframe block
// Multi-brand: noir / pulse / crimson / mono

export type ImmersivePortfolioBrand = 'noir' | 'pulse' | 'crimson' | 'mono'
export interface ImmersivePortfolioPageData { brand?: ImmersivePortfolioBrand }
export const DEFAULT_IMMERSIVE_PORTFOLIO_DATA: ImmersivePortfolioPageData = { brand: 'noir' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const IMMERSIVE_PORTFOLIO_CSS = `
/* ============ БАЗА / ТЕМЫ ============ */
:root{
  --head-h:76px;
  --bg:#0b0a08; --bg2:#151109; --ink:#efe6d3; --muted:#9d9077;
  --accent:#c9a35c; --line:rgba(239,230,211,.14);
  --font-display:'Cormorant Garamond',Georgia,serif;
  --font-body:'Jost',system-ui,sans-serif;
  --font-mono:'JetBrains Mono',ui-monospace,monospace;
  --img-fx:sepia(.25) saturate(.95) contrast(1.06) brightness(.92);
  --vig:rgba(201,163,92,.12);
}
[data-theme="pulse"]{
  --bg:#04090b; --bg2:#0a1418; --ink:#dcf6f4; --muted:#5f8184;
  --accent:#2ee6d6; --line:rgba(220,246,244,.12);
  --font-display:'Unbounded',system-ui,sans-serif;
  --font-body:'Manrope',system-ui,sans-serif;
  --img-fx:saturate(1.2) hue-rotate(6deg) contrast(1.1) brightness(.88);
  --vig:rgba(46,230,214,.10);
}
[data-theme="crimson"]{
  --bg:#0c0607; --bg2:#180b0d; --ink:#f4e8e4; --muted:#a08480;
  --accent:#e5342c; --line:rgba(244,232,228,.13);
  --font-display:'Oswald',system-ui,sans-serif;
  --font-body:'Manrope',system-ui,sans-serif;
  --img-fx:sepia(.15) hue-rotate(-10deg) saturate(1.25) contrast(1.08) brightness(.9);
  --vig:rgba(229,52,44,.12);
}
[data-theme="mono"]{
  --bg:#0e0e0e; --bg2:#171717; --ink:#ececec; --muted:#8f8f8f;
  --accent:#f2f2f2; --line:rgba(236,236,236,.15);
  --font-display:'Manrope',system-ui,sans-serif;
  --font-body:'Manrope',system-ui,sans-serif;
  --img-fx:grayscale(1) contrast(1.12) brightness(.9);
  --vig:rgba(255,255,255,.07);
}
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{
  background:var(--bg); color:var(--ink);
  font-family:var(--font-body); font-weight:400;
  line-height:1.6; overflow-x:hidden;
  transition:background-color .7s ease,color .7s ease;
}
img{display:block;max-width:100%;filter:var(--img-fx);transition:filter .9s ease,transform 1.2s cubic-bezier(.2,.6,.2,1)}
a{color:inherit;text-decoration:none}
button{font:inherit;color:inherit;background:none;border:none;cursor:pointer}
::selection{background:var(--accent);color:var(--bg)}
.container{width:min(1280px,92vw);margin-inline:auto}
.mono{font-family:var(--font-mono)}
section{scroll-margin-top:calc(var(--head-h) + 20px)}
h1,h2,h3{font-family:var(--font-display);font-weight:600;line-height:1.05}
[data-theme="noir"] .display-xl{font-style:italic;font-weight:600}
[data-theme="pulse"] .display-xl{font-weight:800}
[data-theme="crimson"] .display-xl{font-weight:600;text-transform:uppercase}
[data-theme="mono"] .display-xl{font-weight:800;text-transform:uppercase;letter-spacing:-.03em}
.kicker{font-size:11px;letter-spacing:.42em;text-transform:uppercase;color:var(--accent);margin-bottom:22px}
.sec-title{font-size:clamp(2.3rem,5.6vw,4.4rem)}
.acc{color:var(--accent)}

/* ============ ПЛЁНКА / FX ============ */
.grain{position:fixed;inset:-40px;z-index:9990;pointer-events:none;opacity:.07;
  background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  animation:grain 1.2s steps(4) infinite}
@keyframes grain{0%{transform:translate(0,0)}25%{transform:translate(-12px,8px)}50%{transform:translate(10px,-10px)}75%{transform:translate(-8px,-6px)}100%{transform:translate(0,0)}}
.fx{position:fixed;inset:0;pointer-events:none;opacity:0;transition:opacity .9s ease;z-index:1}
.fx-scan{background:repeating-linear-gradient(0deg,transparent 0 3px,rgba(0,0,0,.28) 3px 4px)}
.fx-grid{background:
  linear-gradient(var(--line) 1px,transparent 1px) 0 0/100% 25vh,
  linear-gradient(90deg,var(--line) 1px,transparent 1px) 0 0/25vw 100%}
.fx-vig{background:radial-gradient(120% 90% at 50% 50%,transparent 55%,var(--vig) 100%)}
[data-theme="pulse"] .fx-scan{opacity:.5}
[data-theme="mono"] .fx-grid{opacity:.4}
[data-theme="crimson"] .fx-vig,[data-theme="noir"] .fx-vig{opacity:1}
.flash{position:fixed;inset:0;z-index:9980;pointer-events:none;opacity:0;background:var(--accent)}
.flash.go{animation:flashgo .7s ease-out forwards}
@keyframes flashgo{0%{opacity:.16}100%{opacity:0}}
#progress{position:fixed;top:0;left:0;right:0;height:2px;background:var(--accent);transform-origin:0 50%;transform:scaleX(0);z-index:9995}

/* ============ КУРСОР ============ */
.cursor{position:fixed;top:0;left:0;z-index:9999;pointer-events:none;display:none}
@media(pointer:fine){.cursor{display:block}}
.cursor-dot{position:absolute;width:6px;height:6px;background:var(--accent);border-radius:50%;transform:translate(-50%,-50%)}
.cursor-ring{position:absolute;width:36px;height:36px;border:1px solid var(--accent);border-radius:50%;transform:translate(-50%,-50%);transition:width .3s,height .3s,background-color .3s,opacity .3s;opacity:.7}
.cursor.is-hover .cursor-ring{width:64px;height:64px;background:color-mix(in srgb,var(--accent) 12%,transparent);opacity:1}

/* ============ ЛОАДЕР ============ */
#loader{position:fixed;inset:0;z-index:10001;background:var(--bg);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:28px;transition:clip-path 1s cubic-bezier(.8,0,.2,1)}
#loader.done{clip-path:inset(0 0 100% 0)}
.loader-logo{font-family:var(--font-display);font-size:clamp(3rem,10vw,6rem);letter-spacing:.06em}
.loader-bar{width:min(320px,60vw);height:1px;background:var(--line);overflow:hidden}
.loader-bar i{display:block;height:100%;background:var(--accent);transform-origin:0 50%;transform:scaleX(0)}
.loader-num{font-family:var(--font-mono);font-size:12px;letter-spacing:.3em;color:var(--muted)}

/* ============ ШАПКА ============ */
#header{position:fixed;top:0;left:0;right:0;height:var(--head-h);z-index:100;display:flex;align-items:center;justify-content:space-between;padding-inline:4vw;transition:background-color .5s,border-color .5s;border-bottom:1px solid transparent}
#header.scrolled{background:color-mix(in srgb,var(--bg) 78%,transparent);backdrop-filter:blur(14px);border-color:var(--line)}
.logo{font-family:var(--font-display);font-size:1.6rem;letter-spacing:.05em}
[data-theme="crimson"] .logo,[data-theme="mono"] .logo{text-transform:uppercase}
[data-theme="noir"] .logo{font-style:italic}
.logo-reg{font-size:.55em;vertical-align:super;color:var(--accent)}
.nav{display:flex;gap:34px}
.nav a{font-family:var(--font-mono);font-size:11px;letter-spacing:.28em;text-transform:uppercase;color:var(--muted);position:relative;padding:6px 0;transition:color .3s}
.nav a::after{content:"";position:absolute;left:0;bottom:0;height:1px;width:100%;background:var(--accent);transform:scaleX(0);transform-origin:right;transition:transform .45s cubic-bezier(.2,.6,.2,1)}
.nav a:hover{color:var(--ink)}
.nav a:hover::after{transform:scaleX(1);transform-origin:left}
.head-right{display:flex;align-items:center;gap:22px}
.theme-label{font-size:11px;letter-spacing:.3em;color:var(--accent)}
.burger{width:40px;height:40px;position:relative;display:grid;place-items:center}
.burger span{position:absolute;width:24px;height:1.5px;background:var(--ink);transition:transform .45s cubic-bezier(.2,.6,.2,1)}
.burger span:first-child{transform:translateY(-4px)}
.burger span:last-child{transform:translateY(4px)}
body.menu-open .burger span:first-child{transform:rotate(45deg)}
body.menu-open .burger span:last-child{transform:rotate(-45deg)}

/* ============ МЕНЮ ============ */
.menu{position:fixed;inset:0;z-index:95;background:var(--bg2);display:flex;flex-direction:column;justify-content:center;padding:14vh 8vw 6vh;clip-path:inset(0 0 100% 0);transition:clip-path .8s cubic-bezier(.8,0,.2,1);visibility:hidden}
body.menu-open .menu{clip-path:inset(0 0 0% 0);visibility:visible}
.menu-nav{display:flex;flex-direction:column;gap:6px}
.menu-nav a{font-family:var(--font-display);font-size:clamp(2rem,7vw,4rem);display:flex;align-items:baseline;gap:18px;width:max-content;opacity:0;transform:translateY(30px);transition:opacity .6s,transform .6s,color .3s}
[data-theme="noir"] .menu-nav a{font-style:italic}
[data-theme="crimson"] .menu-nav a,[data-theme="mono"] .menu-nav a{text-transform:uppercase}
.menu-nav a i{font-family:var(--font-mono);font-style:normal;font-size:11px;color:var(--accent);letter-spacing:.3em}
.menu-nav a:hover{color:var(--accent);transform:translateX(14px)}
body.menu-open .menu-nav a{opacity:1;transform:none}
body.menu-open .menu-nav a:nth-child(1){transition-delay:.15s}body.menu-open .menu-nav a:nth-child(2){transition-delay:.22s}body.menu-open .menu-nav a:nth-child(3){transition-delay:.29s}body.menu-open .menu-nav a:nth-child(4){transition-delay:.36s}body.menu-open .menu-nav a:nth-child(5){transition-delay:.43s}
.menu-themes{margin-top:7vh;display:flex;flex-direction:column;gap:14px}
.menu-themes>span{font-size:10px;letter-spacing:.4em;color:var(--muted)}
.menu-themes-row{display:flex;gap:12px;flex-wrap:wrap}
.menu-foot{margin-top:5vh;font-size:11px;letter-spacing:.25em;color:var(--muted)}

/* ============ РЕЙЛ ТЕМ / ТАЙМКОД ============ */
.rail{position:fixed;left:22px;bottom:26px;z-index:96;display:flex;flex-direction:column;gap:14px}
.rail-btn{width:14px;height:14px;border-radius:50%;border:1px solid var(--line);background:var(--c);position:relative;opacity:.55;transition:opacity .3s,transform .3s}
.rail-btn em{position:absolute;left:24px;top:50%;transform:translateY(-50%);font-style:normal;font-family:var(--font-mono);font-size:10px;letter-spacing:.28em;color:var(--muted);white-space:nowrap;opacity:0;transition:opacity .3s;pointer-events:none}
.rail-btn:hover em{opacity:1}
.rail-btn.is-active{opacity:1;transform:scale(1.35);box-shadow:0 0 0 4px color-mix(in srgb,var(--c) 22%,transparent)}
.timecode{position:fixed;right:22px;bottom:26px;z-index:96;font-size:10px;letter-spacing:.28em;color:var(--muted);display:flex;align-items:center;gap:8px}
.timecode i{width:7px;height:7px;border-radius:50%;background:#e5342c;animation:blink 1.4s steps(2) infinite}
@keyframes blink{50%{opacity:.15}}

/* ============ HERO ============ */
.hero{position:relative;min-height:100svh;display:flex;align-items:flex-end;overflow:hidden;z-index:2}
.hero-bg{position:absolute;inset:0}
.hero-bg img{width:100%;height:100%;object-fit:cover;animation:kb 24s ease-in-out infinite alternate}
@keyframes kb{from{transform:scale(1.06)}to{transform:scale(1.18) translate(-1.5%,1.5%)}}
.hero-shade{position:absolute;inset:0;background:linear-gradient(180deg,color-mix(in srgb,var(--bg) 30%,transparent) 0%,transparent 35%,color-mix(in srgb,var(--bg) 88%,transparent) 88%),linear-gradient(90deg,color-mix(in srgb,var(--bg) 55%,transparent),transparent 50%)}
.hero-inner{position:relative;z-index:3;padding-bottom:13vh;padding-top:calc(var(--head-h) + 6vh)}
.hero-kicker{font-size:11px;letter-spacing:.42em;color:var(--accent);opacity:0;transform:translateY(16px);transition:opacity .8s .5s,transform .8s .5s}
.display-xl{font-size:clamp(5.4rem,24vw,20rem);line-height:.88;letter-spacing:.01em}
.display-xl sup{font-size:.14em;color:var(--accent);vertical-align:super}
.hero .display-xl{opacity:0;transform:translateY(6vh);transition:opacity 1s .7s,transform 1s .7s cubic-bezier(.2,.6,.2,1)}
.hero-sub{margin-top:3vh;max-width:520px;font-size:clamp(1rem,1.6vw,1.25rem);color:var(--muted);transition:opacity .35s,transform .35s}
.hero-sub.fade-out{opacity:0;transform:translateY(10px)}
.hero-meta{margin-top:5vh;display:flex;gap:34px;flex-wrap:wrap;font-size:10px;letter-spacing:.3em;color:var(--muted);opacity:0;transform:translateY(16px);transition:opacity .8s 1.2s,transform .8s 1.2s}
.hero-meta span{border-left:1px solid var(--line);padding-left:14px}
body.loaded .hero-kicker,body.loaded .hero-meta{opacity:1;transform:none}
body.loaded .hero .display-xl{opacity:1;transform:none}
.hero-side{position:absolute;right:26px;top:50%;transform:translateY(-50%);writing-mode:vertical-rl;font-size:10px;letter-spacing:.5em;color:var(--muted);z-index:3}
.hero-scroll{position:absolute;left:4vw;bottom:0;z-index:3;display:flex;flex-direction:column;align-items:center;gap:12px;font-size:9px;letter-spacing:.4em;color:var(--muted)}
.hero-scroll-line{width:1px;height:64px;background:var(--line);position:relative;overflow:hidden}
.hero-scroll-line::after{content:"";position:absolute;inset:0;background:var(--accent);transform:translateY(-100%);animation:scrollline 2.2s cubic-bezier(.7,0,.3,1) infinite}
@keyframes scrollline{40%{transform:translateY(0)}100%{transform:translateY(100%)}}

/* ============ MARQUEE ============ */
.marquee{overflow:hidden;border-block:1px solid var(--line);padding:20px 0;position:relative;z-index:2;background:var(--bg)}
.marquee-track{display:flex;width:max-content;animation:mq 26s linear infinite}
.marquee:hover .marquee-track{animation-play-state:paused}
.marquee-track span{font-family:var(--font-mono);font-size:12px;letter-spacing:.4em;text-transform:uppercase;color:var(--muted);white-space:nowrap;padding:0 26px}
.marquee-track span b{color:var(--accent);font-weight:400}
@keyframes mq{to{transform:translateX(-50%)}}
.marquee--big{padding:30px 0}
.marquee--big .marquee-track span{font-family:var(--font-display);font-size:clamp(1.6rem,4vw,3rem);letter-spacing:.06em;color:var(--ink)}
[data-theme="noir"] .marquee--big .marquee-track span{font-style:italic}
[data-theme="crimson"] .marquee--big .marquee-track span,[data-theme="mono"] .marquee--big .marquee-track span{text-transform:uppercase}

/* ============ REVEAL ============ */
[data-reveal]{opacity:0;transform:translateY(36px);transition:opacity .9s ease var(--d,0s),transform .9s cubic-bezier(.2,.6,.2,1) var(--d,0s)}
[data-reveal].is-in{opacity:1;transform:none}
.lm{display:block;overflow:hidden}
.lm>span{display:block;transform:translateY(112%);transition:transform 1.1s cubic-bezier(.2,.65,.15,1)}
.lm:nth-child(2)>span{transition-delay:.12s}
.split.is-in .lm>span{transform:none}

/* ============ СТУДИЯ ============ */
.about{display:grid;grid-template-columns:1fr 1fr;gap:6vw;padding-block:16vh;position:relative;z-index:2}
.about-left{position:sticky;top:calc(var(--head-h) + 30px);align-self:start}
.about-photo{margin-top:6vh;max-width:420px;overflow:hidden;position:relative}
.about-photo img{width:100%;aspect-ratio:4/5;object-fit:cover;animation:breathe 9s ease-in-out infinite alternate}
@keyframes breathe{from{transform:scale(1)}to{transform:scale(1.07)}}
.about-photo figcaption{margin-top:12px;font-size:10px;letter-spacing:.32em;color:var(--muted)}
.about-right .lead{font-size:clamp(1.2rem,2vw,1.6rem);line-height:1.5;margin-bottom:26px}
.about-right p{color:var(--muted);margin-bottom:22px;max-width:56ch}
.stats{display:grid;grid-template-columns:repeat(2,1fr);gap:1px;background:var(--line);border:1px solid var(--line);margin:52px 0}
.stat{background:var(--bg);padding:28px 24px;transition:background-color .7s}
.stat b{display:block;font-family:var(--font-display);font-size:clamp(2.2rem,4vw,3.4rem);color:var(--accent);line-height:1}
[data-theme="noir"] .stat b{font-style:italic}
.stat span{font-family:var(--font-mono);font-size:10px;letter-spacing:.3em;color:var(--muted);text-transform:uppercase}
blockquote{border-left:2px solid var(--accent);padding-left:24px;font-family:var(--font-display);font-size:1.3rem;font-style:italic;color:var(--ink)}

/* ============ РАБОТЫ ============ */
.works{padding-block:14vh 8vh;position:relative;z-index:2}
.works-head{display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-bottom:9vh}
.works-head p.note{font-family:var(--font-mono);font-size:11px;letter-spacing:.3em;color:var(--muted)}
.stack{display:flex;flex-direction:column}
.work-card{position:sticky;top:calc(var(--head-h) + 18px + var(--i)*20px);min-height:76vh;margin-bottom:9vh;background:var(--bg2);border:1px solid var(--line);display:grid;grid-template-columns:1.15fr .85fr;overflow:hidden;transform-origin:top center;will-change:transform;transition:background-color .7s,border-color .7s}
.work-card:last-child{margin-bottom:0}
.work-idx{position:absolute;right:2vw;top:2vh;font-size:clamp(4rem,10vw,9rem);color:transparent;-webkit-text-stroke:1px var(--line);z-index:2;pointer-events:none;line-height:1}
.work-media{overflow:hidden;position:relative;min-height:320px}
.work-media img{width:100%;height:100%;object-fit:cover}
.work-card:hover .work-media img{transform:scale(1.07)}
.work-media::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent 60%,var(--bg2))}
.work-info{padding:clamp(26px,4vw,56px);display:flex;flex-direction:column;justify-content:space-between;gap:26px}
.work-tags{display:flex;gap:18px;flex-wrap:wrap;font-size:10px;letter-spacing:.3em;color:var(--muted)}
.work-tags span{border:1px solid var(--line);padding:6px 12px}
.work-title{font-size:clamp(2.2rem,4.6vw,4rem);transition:color .4s}
.work-card:hover .work-title{color:var(--accent)}
.work-desc{color:var(--muted);max-width:38ch}
.work-link{font-size:11px;letter-spacing:.32em;color:var(--accent);display:flex;align-items:center;gap:10px;width:max-content}
.work-link span{display:inline-block;transition:transform .4s}
.work-link:hover span{transform:translate(6px,-6px)}

/* ============ УСЛУГИ ============ */
.services{padding-block:16vh;position:relative;z-index:2}
.svc-list{margin-top:7vh;border-bottom:1px solid var(--line)}
.svc{display:grid;grid-template-columns:90px 1fr auto;gap:26px;align-items:center;padding:40px 10px;border-top:1px solid var(--line);transition:background-color .4s,padding .4s}
.svc:hover{background:color-mix(in srgb,var(--bg2) 70%,transparent);padding-left:26px}
.svc-num{font-size:12px;letter-spacing:.3em;color:var(--accent)}
.svc-title{font-size:clamp(1.4rem,2.6vw,2.2rem);transition:transform .4s,color .4s}
.svc:hover .svc-title{transform:translateX(10px);color:var(--accent)}
.svc-desc{color:var(--muted);font-size:.95rem;margin-top:6px}
.svc-arrow{font-size:1.6rem;color:var(--muted);transition:transform .4s,color .4s}
.svc:hover .svc-arrow{transform:translate(6px,-6px);color:var(--accent)}
.svc-preview{position:fixed;z-index:60;width:280px;aspect-ratio:4/3;pointer-events:none;opacity:0;transform:translate(-50%,-50%) scale(.85) rotate(-3deg);transition:opacity .35s,transform .35s;overflow:hidden;border:1px solid var(--line)}
.svc-preview.on{opacity:1;transform:translate(-50%,-50%) scale(1) rotate(-3deg)}
.svc-preview img{width:100%;height:100%;object-fit:cover}

/* ============ НАГРАДЫ ============ */
.awards{padding-block:6vh 16vh;position:relative;z-index:2}
.awards ul{list-style:none;margin-top:6vh;border-bottom:1px solid var(--line)}
.awards li{display:grid;grid-template-columns:110px 1.2fr 1fr auto;gap:22px;align-items:baseline;padding:26px 10px;border-top:1px solid var(--line);transition:padding .4s,background-color .4s}
.awards li:hover{background:color-mix(in srgb,var(--bg2) 70%,transparent);padding-left:26px}
.awards .a-year{font-family:var(--font-mono);font-size:11px;letter-spacing:.3em;color:var(--accent)}
.awards .a-name{font-family:var(--font-display);font-size:1.3rem}
[data-theme="noir"] .awards .a-name{font-style:italic}
.awards .a-proj{font-family:var(--font-mono);font-size:11px;letter-spacing:.24em;color:var(--muted)}
.awards .a-x{color:var(--muted)}

/* ============ CTA / ФУТЕР ============ */
.cta{position:relative;overflow:hidden;border-top:1px solid var(--line);padding:16vh 0 10vh;z-index:2;background:var(--bg2);transition:background-color .7s}
.cta-title{font-size:clamp(3rem,10vw,8.4rem);line-height:.95}
.cta-bg{position:absolute;right:-2vw;bottom:-6vh;font-family:var(--font-display);font-size:clamp(10rem,34vw,30rem);color:transparent;-webkit-text-stroke:1px var(--line);line-height:1;pointer-events:none;user-select:none}
.cta-mail{display:inline-block;margin-top:6vh;font-family:var(--font-mono);font-size:clamp(1.1rem,3.4vw,2.4rem);letter-spacing:.06em;color:var(--ink);position:relative}
.cta-mail::after{content:"";position:absolute;left:0;bottom:-8px;height:1px;width:100%;background:var(--accent);transform:scaleX(0);transform-origin:right;transition:transform .6s cubic-bezier(.2,.6,.2,1)}
.cta-mail:hover{color:var(--accent)}
.cta-mail:hover::after{transform:scaleX(1);transform-origin:left}
.socials{display:flex;gap:26px;flex-wrap:wrap;margin-top:7vh}
.socials a{font-family:var(--font-mono);font-size:11px;letter-spacing:.3em;color:var(--muted);transition:color .3s,transform .3s}
.socials a:hover{color:var(--accent);transform:translateY(-4px)}
.foot{display:flex;justify-content:space-between;align-items:center;gap:18px;padding-block:30px;font-family:var(--font-mono);font-size:10px;letter-spacing:.28em;color:var(--muted);position:relative;z-index:2;flex-wrap:wrap}
#toTop{font-family:var(--font-mono);font-size:10px;letter-spacing:.28em;color:var(--accent);border:1px solid var(--line);padding:12px 20px;transition:background-color .3s,color .3s}
#toTop:hover{background:var(--accent);color:var(--bg)}

/* ============ АДАПТИВ ============ */
@media(max-width:1024px){
  .nav{display:none}
  .about{grid-template-columns:1fr;gap:8vh}
  .about-left{position:static}
  .hero-side{display:none}
}
@media(max-width:820px){
  .work-card{position:relative;top:0;grid-template-columns:1fr;min-height:0;margin-bottom:6vh}
  .work-media{min-height:0;aspect-ratio:16/10}
  .work-media::after{background:linear-gradient(0deg,var(--bg2),transparent 40%)}
  .awards li{grid-template-columns:70px 1fr;gap:8px 16px}
  .awards .a-proj{grid-column:2}
  .awards .a-x{display:none}
  .timecode{display:none}
  .svc{grid-template-columns:44px 1fr auto;gap:14px}
}
@media(max-width:560px){
  .hero-meta{gap:14px}
  .hero-meta span{padding-left:10px}
  .rail{left:14px;bottom:16px;flex-direction:row;gap:12px}
  .rail-btn em{display:none}
  .stats{grid-template-columns:1fr 1fr}
  .works-head{flex-direction:column;align-items:flex-start}
  .theme-label{display:none}
}
/* ============ REDUCED MOTION ============ */
@media(prefers-reduced-motion:reduce){
  *,*::before,*::after{animation:none!important;transition:none!important}
  html{scroll-behavior:auto}
  .cursor{display:none!important}
  [data-reveal],.hero-kicker,.hero-meta,.hero .display-xl{opacity:1!important;transform:none!important}
  .lm>span{transform:none!important}
  #loader{display:none}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const IMMERSIVE_PORTFOLIO_HTML_BODY = `
<div id="loader" aria-hidden="true">
  <div class="loader-logo display-xl" id="loaderLogo">BST</div>
  <div class="loader-bar"><i id="loaderBar"></i></div>
  <div class="loader-num" id="loaderNum">000</div>
</div>

<div id="progress"></div>
<div class="grain" aria-hidden="true"></div>
<div class="fx fx-scan" aria-hidden="true"></div>
<div class="fx fx-grid" aria-hidden="true"></div>
<div class="fx fx-vig" aria-hidden="true"></div>
<div class="flash" id="flash" aria-hidden="true"></div>
<div class="cursor" id="cursor" aria-hidden="true"><span class="cursor-ring"></span><span class="cursor-dot"></span></div>

<header id="header">
  <a class="logo" href="#top">BST<span class="logo-reg">®</span></a>
  <nav class="nav">
    <a href="#studio">Студия</a>
    <a href="#works">Работы</a>
    <a href="#services">Услуги</a>
    <a href="#awards">Награды</a>
    <a href="#contact">Контакт</a>
  </nav>
  <div class="head-right">
    <span class="theme-label mono" id="themeLabel">01 / NOIR</span>
    <button class="burger" id="burger" aria-label="Открыть меню"><span></span><span></span></button>
  </div>
</header>

<div class="menu" id="menu">
  <nav class="menu-nav">
    <a href="#studio"><i>01</i>Студия</a>
    <a href="#works"><i>02</i>Работы</a>
    <a href="#services"><i>03</i>Услуги</a>
    <a href="#awards"><i>04</i>Награды</a>
    <a href="#contact"><i>05</i>Контакт</a>
  </nav>
  <div class="menu-themes">
    <span class="mono">ВАРИАЦИИ БРЕНДА BST</span>
    <div class="menu-themes-row">
      <button class="rail-btn is-active" data-theme-btn="noir" style="--c:#c9a35c" aria-label="Тема NOIR"><em>01 NOIR</em></button>
      <button class="rail-btn" data-theme-btn="pulse" style="--c:#2ee6d6" aria-label="Тема PULSE"><em>02 PULSE</em></button>
      <button class="rail-btn" data-theme-btn="crimson" style="--c:#e5342c" aria-label="Тема CRIMSON"><em>03 CRIMSON</em></button>
      <button class="rail-btn" data-theme-btn="mono" style="--c:#f2f2f2" aria-label="Тема MONO"><em>04 MONO</em></button>
    </div>
  </div>
  <div class="menu-foot mono">HELLO@BST.STUDIO · TG / BE / IG</div>
</div>

<aside class="rail" aria-label="Переключение вариаций бренда">
  <button class="rail-btn is-active" data-theme-btn="noir" style="--c:#c9a35c" aria-label="Вариация NOIR"><em>01 NOIR</em></button>
  <button class="rail-btn" data-theme-btn="pulse" style="--c:#2ee6d6" aria-label="Вариация PULSE"><em>02 PULSE</em></button>
  <button class="rail-btn" data-theme-btn="crimson" style="--c:#e5342c" aria-label="Вариация CRIMSON"><em>03 CRIMSON</em></button>
  <button class="rail-btn" data-theme-btn="mono" style="--c:#f2f2f2" aria-label="Вариация MONO"><em>04 MONO</em></button>
</aside>
<div class="timecode mono" id="timecode"><i></i><span>REC 00:00:00:00</span></div>

<main>
<!-- ============ HERO ============ -->
<section class="hero" id="top">
  <figure class="hero-bg"><img data-img="heroBg" src="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/1a7c9c89a-3a16-4695-8269-76972e6ad767.png" alt="Кинематографичная студия BST"></figure>
  <div class="hero-shade"></div>
  <div class="hero-inner container">
    <p class="hero-kicker mono" id="sceneLabel">СЦЕНА 01 · КИНО</p>
    <h1 class="display-xl"><span id="heroTitle">BST</span><sup>®</sup></h1>
    <p class="hero-sub" id="heroSub">Бренды, снятые как кино: свет, тишина и золотая пыль.</p>
    <div class="hero-meta mono">
      <span>ИММЕРСИВНАЯ СТУДИЯ ДИЗАЙНА</span>
      <span>МОСКВА — ДУБАЙ — БЕРЛИН</span>
      <span>ОТКРЫТЫ К ПРОЕКТАМ · Q3 2025</span>
    </div>
  </div>
  <div class="hero-side mono">ПОРТФОЛИО — 2025 — КАДР 001</div>
  <a class="hero-scroll mono" href="#studio"><span class="hero-scroll-line"></span>СМОТРЕТЬ ВНИЗ</a>
</section>

<div class="marquee" aria-hidden="true">
  <div class="marquee-track">
    <span>ИДЕНТИЧНОСТЬ <b>✦</b> СТРАТЕГИЯ <b>✦</b> ВЕБ-ДИЗАЙН <b>✦</b> МОУШН <b>✦</b> 3D <b>✦</b> АРТ-ДИРЕКШН <b>✦</b> САУНД <b>✦</b> УПАКОВКА <b>✦</b>&nbsp;</span>
    <span>ИДЕНТИЧНОСТЬ <b>✦</b> СТРАТЕГИЯ <b>✦</b> ВЕБ-ДИЗАЙН <b>✦</b> МОУШН <b>✦</b> 3D <b>✦</b> АРТ-ДИРЕКШН <b>✦</b> САУНД <b>✦</b> УПАКОВКА <b>✦</b>&nbsp;</span>
  </div>
</div>

<!-- ============ СТУДИЯ ============ -->
<section class="about container" id="studio">
  <div class="about-left">
    <p class="kicker mono" data-scramble>01 — СТУДИЯ</p>
    <h2 class="sec-title split">
      <span class="lm"><span data-t="aboutTitle1">Не студия.</span></span>
      <span class="lm"><span data-t="aboutTitle2">Съёмочная <em class="acc">группа.</em></span></span>
    </h2>
    <figure class="about-photo" data-reveal>
      <img data-img="aboutImg" src="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/1043e601f-6076-4814-9b37-63eae60df4a7.png" alt="Режиссёр кадра BST">
      <figcaption class="mono" data-t="aboutCaption">РЕЖИССЁР КАДРА — BST</figcaption>
    </figure>
  </div>
  <div class="about-right">
    <p class="lead" data-reveal data-t="studioText1">BST — независимая студия иммерсивного дизайна. Мы собираем бренд как фильм: сценарий стратегии, свет айдентики, монтаж интерфейса.</p>
    <p data-reveal data-t="studioText2" style="--d:.1s">Каждый проект получает собственный жанр — нуар, неон, кримсон или моно. Меняется всё: типографика, цвет, ритм, звук. Не меняется одно — кинематографическое качество кадра.</p>
    <p data-reveal data-t="studioText3" style="--d:.15s">Переключите вариацию бренда в левом нижнем углу — и посмотрите, как одна студия говорит на четырёх языках.</p>
    <div class="stats" data-reveal>
      <div class="stat"><b data-count="6">0</b><span data-t="stat1L">лет в кадре</span></div>
      <div class="stat"><b data-count="47">0</b><span data-t="stat2L">проектов</span></div>
      <div class="stat"><b data-count="12">0</b><span data-t="stat3L">наград</span></div>
      <div class="stat"><b data-count="4">0</b><span data-t="stat4L">стилевых вселенных</span></div>
    </div>
    <blockquote data-reveal data-t="studioQuote">«Стиль — это монтаж. Всё остальное — шум плёнки.»</blockquote>
  </div>
</section>

<!-- ============ РАБОТЫ ============ -->
<section class="works" id="works">
  <header class="works-head container">
    <div>
      <p class="kicker mono" data-scramble>02 — ИЗБРАННОЕ</p>
      <h2 class="sec-title split">
        <span class="lm"><span>Афиша</span></span>
        <span class="lm"><span>сезона <em class="acc">'23—'25</em></span></span>
      </h2>
    </div>
    <p class="note">06 КАДРОВ / СКРОЛЛ — КАК МОНТАЖ</p>
  </header>

  <div class="stack container">
    <article class="work-card" style="--i:0">
      <span class="work-idx">01</span>
      <figure class="work-media"><img data-img="p1img" src="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/1821bf0cf-a4ad-4ead-b803-cc6c0acc6583.png" alt="AURUM"></figure>
      <div class="work-info">
        <div class="work-tags mono" data-t="p1Tags"><span>АЙДЕНТИКА</span><span>УПАКОВКА</span><span>2025</span></div>
        <h3 class="work-title" data-t="p1Title">AURUM</h3>
        <p class="work-desc" data-t="p1Desc">Парфюмерный дом: золото, тень и дым. Нуар в каждом флаконе.</p>
        <a class="work-link mono" href="#contact">СМОТРЕТЬ КЕЙС <span>↗</span></a>
      </div>
    </article>

    <article class="work-card" style="--i:1">
      <span class="work-idx">02</span>
      <figure class="work-media"><img data-img="p2img" src="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/1398cfd97-d856-45da-be1d-c6c44774cb28.png" alt="NEON DISTRICT"></figure>
      <div class="work-info">
        <div class="work-tags mono" data-t="p2Tags"><span>ЦИФРОВОЙ ПРОДУКТ</span><span>WEB</span><span>2025</span></div>
        <h3 class="work-title" data-t="p2Title">NEON DISTRICT</h3>
        <p class="work-desc" data-t="p2Desc">Квартал, который не спит. Иммерсивная платформа ночного города.</p>
        <a class="work-link mono" href="#contact">СМОТРЕТЬ КЕЙС <span>↗</span></a>
      </div>
    </article>

    <article class="work-card" style="--i:2">
      <span class="work-idx">03</span>
      <figure class="work-media"><img data-img="p3img" src="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/154ac9b01-b1ac-4532-bdf8-f7868e5db843.png" alt="SCARLET"></figure>
      <div class="work-info">
        <div class="work-tags mono" data-t="p3Tags"><span>FASHION</span><span>EDITORIAL</span><span>2024</span></div>
        <h3 class="work-title" data-t="p3Title">SCARLET</h3>
        <p class="work-desc" data-t="p3Desc">Красный как манифест. Редакционная съёмка и айдентика дома моды.</p>
        <a class="work-link mono" href="#contact">СМОТРЕТЬ КЕЙС <span>↗</span></a>
      </div>
    </article>

    <article class="work-card" style="--i:3">
      <span class="work-idx">04</span>
      <figure class="work-media"><img data-img="p4img" src="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/173f32add-52be-4937-9776-18b02d8b64e7.png" alt="MONOLITH"></figure>
      <div class="work-info">
        <div class="work-tags mono" data-t="p4Tags"><span>АРХИТЕКТУРА</span><span>БРЕНДИНГ</span><span>2024</span></div>
        <h3 class="work-title" data-t="p4Title">MONOLITH</h3>
        <p class="work-desc" data-t="p4Desc">Бетон, свет, тишина. Бюро архитектуры и монументального дизайна.</p>
        <a class="work-link mono" href="#contact">СМОТРЕТЬ КЕЙС <span>↗</span></a>
      </div>
    </article>

    <article class="work-card" style="--i:4">
      <span class="work-idx">05</span>
      <figure class="work-media"><img data-img="p5img" src="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/1ba66aaea-7dc6-4753-ac8c-41718a07bd33.png" alt="VELOCITY"></figure>
      <div class="work-info">
        <div class="work-tags mono" data-t="p5Tags"><span>AUTOMOTIVE</span><span>КАМПАНИЯ</span><span>2023</span></div>
        <h3 class="work-title" data-t="p5Title">VELOCITY</h3>
        <p class="work-desc" data-t="p5Desc">Скорость, снятая на длинной выдержке. Launch-кампания суперкара.</p>
        <a class="work-link mono" href="#contact">СМОТРЕТЬ КЕЙС <span>↗</span></a>
      </div>
    </article>

    <article class="work-card" style="--i:5">
      <span class="work-idx">06</span>
      <figure class="work-media"><img data-img="p6img" src="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/121b7ae04-1550-4e30-913e-a1a67e58f097.png" alt="CHRONO"></figure>
      <div class="work-info">
        <div class="work-tags mono" data-t="p6Tags"><span>ПРОДУКТ</span><span>3D</span><span>2023</span></div>
        <h3 class="work-title" data-t="p6Title">CHRONO</h3>
        <p class="work-desc" data-t="p6Desc">Время крупным планом. 3D-фильм для часовой мануфактуры.</p>
        <a class="work-link mono" href="#contact">СМОТРЕТЬ КЕЙС <span>↗</span></a>
      </div>
    </article>
  </div>
</section>

<div class="marquee marquee--big" aria-hidden="true">
  <div class="marquee-track">
    <span>СНИМАЕМ БРЕНДЫ <b>✦</b> КАК КИНО <b>✦</b> СНИМАЕМ БРЕНДЫ <b>✦</b> КАК КИНО <b>✦</b>&nbsp;</span>
    <span>СНИМАЕМ БРЕНДЫ <b>✦</b> КАК КИНО <b>✦</b> СНИМАЕМ БРЕНДЫ <b>✦</b> КАК КИНО <b>✦</b>&nbsp;</span>
  </div>
</div>

<!-- ============ УСЛУГИ ============ -->
<section class="services container" id="services">
  <p class="kicker mono" data-scramble>03 — УСЛУГИ</p>
  <h2 class="sec-title split">
    <span class="lm"><span>Что мы делаем</span></span>
    <span class="lm"><span>в <em class="acc">кадре</em></span></span>
  </h2>
  <div class="svc-list">
    <div class="svc" data-img="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/173f32add-52be-4937-9776-18b02d8b64e7.png">
      <span class="svc-num mono">01</span>
      <div><h3 class="svc-title" data-t="svc1Title">Стратегия и позиционирование</h3><p class="svc-desc" data-t="svc1Desc">Сценарий бренда до первого пикселя.</p></div>
      <span class="svc-arrow">↗</span>
    </div>
    <div class="svc" data-img="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/1821bf0cf-a4ad-4ead-b803-cc6c0acc6583.png">
      <span class="svc-num mono">02</span>
      <div><h3 class="svc-title" data-t="svc2Title">Айдентика и дизайн-системы</h3><p class="svc-desc" data-t="svc2Desc">Логотип, свет, цвет, характер.</p></div>
      <span class="svc-arrow">↗</span>
    </div>
    <div class="svc" data-img="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/1398cfd97-d856-45da-be1d-c6c44774cb28.png">
      <span class="svc-num mono">03</span>
      <div><h3 class="svc-title" data-t="svc3Title">Веб-дизайн и разработка</h3><p class="svc-desc" data-t="svc3Desc">Иммерсивные сайты с кино-ритмом.</p></div>
      <span class="svc-arrow">↗</span>
    </div>
    <div class="svc" data-img="https://image.qwenlm.ai/public_source/3982cd33-a5be-4a8c-aaf2-9b29b9369f9d/121b7ae04-1550-4e30-913e-a1a67e58f097.png">
      <span class="svc-num mono">04</span>
      <div><h3 class="svc-title" data-t="svc4Title">Моушн, 3D и саунд</h3><p class="svc-desc" data-t="svc4Desc">Движение и звук как часть бренда.</p></div>
      <span class="svc-arrow">↗</span>
    </div>
  </div>
</section>
<div class="svc-preview" id="svcPreview" aria-hidden="true"><img alt=""></div>

<!-- ============ НАГРАДЫ ============ -->
<section class="awards container" id="awards">
  <p class="kicker mono" data-scramble>04 — ПРИЗНАНИЕ</p>
  <h2 class="sec-title split">
    <span class="lm"><span>Награды</span></span>
    <span class="lm"><span>и <em class="acc">фестивали</em></span></span>
  </h2>
  <ul>
    <li data-reveal><span class="a-year">2025</span><span class="a-name" data-t="aw1Name">Awwwards — Site of the Day ×3</span><span class="a-proj" data-t="aw1Proj">NEON DISTRICT</span><span class="a-x mono">✦</span></li>
    <li data-reveal style="--d:.05s"><span class="a-year">2025</span><span class="a-name" data-t="aw2Name">FWA of the Day</span><span class="a-proj" data-t="aw2Proj">AURUM</span><span class="a-x mono">✦</span></li>
    <li data-reveal style="--d:.1s"><span class="a-year">2024</span><span class="a-name" data-t="aw3Name">CSS Design Awards — Best UI</span><span class="a-proj" data-t="aw3Proj">SCARLET</span><span class="a-x mono">✦</span></li>
    <li data-reveal style="--d:.15s"><span class="a-year">2024</span><span class="a-name" data-t="aw4Name">Red Dot — Brands &amp; Communication</span><span class="a-proj" data-t="aw4Proj">MONOLITH</span><span class="a-x mono">✦</span></li>
    <li data-reveal style="--d:.2s"><span class="a-year">2023</span><span class="a-name" data-t="aw5Name">Behance — Featured Gallery</span><span class="a-proj" data-t="aw5Proj">VELOCITY</span><span class="a-x mono">✦</span></li>
    <li data-reveal style="--d:.25s"><span class="a-year">2023</span><span class="a-name" data-t="aw6Name">ADCR — Silver Cube</span><span class="a-proj" data-t="aw6Proj">CHRONO</span><span class="a-x mono">✦</span></li>
  </ul>
</section>

<!-- ============ CTA ============ -->
<section class="cta" id="contact">
  <div class="container">
    <p class="kicker mono" data-scramble>05 — КОНТАКТ</p>
    <h2 class="cta-title split">
      <span class="lm"><span data-t="ctaTitle1">Снимем ваш</span></span>
      <span class="lm"><span data-t="ctaTitle2">бренд<span class="acc">?</span></span></span>
    </h2>
    <a class="cta-mail" href="mailto:hello@bst.studio">hello@bst.studio</a>
    <div class="socials">
      <a href="#top">TELEGRAM ↗</a>
      <a href="#top">BEHANCE ↗</a>
      <a href="#top">INSTAGRAM ↗</a>
      <a href="#top">VIMEO ↗</a>
    </div>
  </div>
  <div class="cta-bg" aria-hidden="true">B</div>
</section>

<footer class="foot container">
  <span>© 2025 BST® STUDIO</span>
  <span>СДЕЛАНО В ТЕМНОТЕ — СВЕТОМ</span>
  <button id="toTop">НАВЕРХ ↑</button>
</footer>
</main>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function immersivePortfolioEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
(function(){
'use strict';
var rm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
var fine = window.matchMedia('(pointer: fine)').matches;
var root = document.documentElement;

/* ---------- SCRAMBLE ---------- */
var CHARS = 'BST#§/\\\\_—0123456789?!*';
function scramble(el, finalText, dur){
  if(!el) return;
  dur = dur || 900;
  if(rm){ el.textContent = finalText; return; }
  var len = finalText.length, start = performance.now();
  (function tick(now){
    var p = Math.min(1, (now - start) / dur);
    var show = Math.floor(p * len), out = '';
    for(var i = 0; i < len; i++){
      out += i < show ? finalText[i] : (finalText[i] === ' ' ? ' ' : CHARS[Math.random() * CHARS.length | 0]);
    }
    el.textContent = out;
    if(p < 1) requestAnimationFrame(tick);
  })(start);
}

/* ---------- ЛОАДЕР ---------- */
var loader = document.getElementById('loader');
var loaderNum = document.getElementById('loaderNum');
var loaderBar = document.getElementById('loaderBar');
function finishLoad(){
  document.body.classList.add('loaded');
  setTimeout(function(){ scramble(document.getElementById('heroTitle'), 'BST', 1100); }, 350);
}
if(!loader || rm){
  if(loader) loader.style.display = 'none';
  finishLoad();
}else{
  scramble(document.getElementById('loaderLogo'), 'BST', 800);
  var t0 = performance.now();
  (function step(now){
    var p = Math.min(100, Math.floor((now - t0) / 13));
    if(loaderNum) loaderNum.textContent = String(p).padStart(3, '0');
    if(loaderBar) loaderBar.style.transform = 'scaleX(' + (p / 100) + ')';
    if(p < 100){ requestAnimationFrame(step); }
    else{
      loader.classList.add('done');
      finishLoad();
      setTimeout(function(){ if(loader.parentNode) loader.remove(); }, 1100);
    }
  })(t0);
}

/* ---------- ТЕМЫ ---------- */
var THEMES = {
  noir:    { name:'NOIR',    num:'01', scene:'СЦЕНА 01 · КИНО',  sub:'Бренды, снятые как кино: свет, тишина и золотая пыль.' },
  pulse:   { name:'PULSE',   num:'02', scene:'СЦЕНА 02 · НЕОН',  sub:'Цифровые продукты со звуком и светом. Неон как язык.' },
  crimson: { name:'CRIMSON', num:'03', scene:'СЦЕНА 03 · МОДА',  sub:'Мода и дерзость. Красный — единственный нейтральный цвет.' },
  mono:    { name:'MONO',    num:'04', scene:'СЦЕНА 04 · ФОРМА', sub:'Архитектура смысла. Тишина как форма, сетка как ритм.' }
};
var themeLabel = document.getElementById('themeLabel');
var sceneLabel = document.getElementById('sceneLabel');
var heroSub = document.getElementById('heroSub');
var flash = document.getElementById('flash');
function applyTheme(key, silent){
  var t = THEMES[key]; if(!t) return;
  root.dataset.theme = key;
  document.title = 'BST® — ' + t.name + ' · Портфолио';
  if(sceneLabel) sceneLabel.textContent = t.scene;
  scramble(themeLabel, t.num + ' / ' + t.name, 600);
  if(heroSub){
    heroSub.classList.add('fade-out');
    setTimeout(function(){ heroSub.textContent = t.sub; heroSub.classList.remove('fade-out'); }, rm ? 0 : 320);
  }
  document.querySelectorAll('[data-theme-btn]').forEach(function(b){
    b.classList.toggle('is-active', b.dataset.themeBtn === key);
  });
  if(!silent && !rm && flash){
    flash.classList.remove('go'); void flash.offsetWidth; flash.classList.add('go');
  }
  try{ localStorage.setItem('bst-theme', key); }catch(e){}
}
document.querySelectorAll('[data-theme-btn]').forEach(function(b){
  b.addEventListener('click', function(){ applyTheme(b.dataset.themeBtn); });
});
var saved = null; try{ saved = localStorage.getItem('bst-theme'); }catch(e){}
applyTheme(saved && THEMES[saved] ? saved : '${brand}', true);

/* ---------- МЕНЮ ---------- */
var burger = document.getElementById('burger');
if(burger){
  burger.addEventListener('click', function(){
    document.body.classList.toggle('menu-open');
  });
  document.querySelectorAll('.menu-nav a').forEach(function(a){
    a.addEventListener('click', function(){ document.body.classList.remove('menu-open'); });
  });
}

/* ---------- REVEAL / SCRAMBLE KICKERS / COUNTERS ---------- */
document.querySelectorAll('[data-scramble]').forEach(function(el){ el.dataset.final = el.textContent; });
var io = new IntersectionObserver(function(entries){
  entries.forEach(function(e){
    if(!e.isIntersecting) return;
    e.target.classList.add('is-in');
    if(e.target.hasAttribute('data-scramble')) scramble(e.target, e.target.dataset.final, 800);
    io.unobserve(e.target);
  });
}, { threshold: .18 });
document.querySelectorAll('[data-reveal], .split, [data-scramble]').forEach(function(el){ io.observe(el); });

var cio = new IntersectionObserver(function(entries){
  entries.forEach(function(e){
    if(!e.isIntersecting) return;
    var el = e.target, target = parseInt(el.dataset.count, 10);
    cio.unobserve(el);
    if(rm){ el.textContent = String(target).padStart(2, '0'); return; }
    var s = performance.now();
    (function cnt(now){
      var p = Math.min(1, (now - s) / 1500);
      var v = Math.round(target * (1 - Math.pow(1 - p, 3)));
      el.textContent = String(v).padStart(2, '0');
      if(p < 1) requestAnimationFrame(cnt);
    })(s);
  });
}, { threshold: .5 });
document.querySelectorAll('[data-count]').forEach(function(el){ cio.observe(el); });

/* ---------- СКРОЛЛ: прогресс, шапка, стек карточек ---------- */
var progress = document.getElementById('progress');
var header = document.getElementById('header');
var cards = Array.prototype.slice.call(document.querySelectorAll('.work-card'));
var ticking = false;
function onScroll(){
  if(ticking) return;
  ticking = true;
  requestAnimationFrame(function(){
    var y = window.scrollY || 0;
    var max = document.documentElement.scrollHeight - innerHeight;
    if(progress) progress.style.transform = 'scaleX(' + (max > 0 ? y / max : 0) + ')';
    if(header) header.classList.toggle('scrolled', y > 40);
    if(!rm && innerWidth > 820){
      for(var i = 0; i < cards.length - 1; i++){
        var r = cards[i + 1].getBoundingClientRect();
        var p = Math.min(1, Math.max(0, 1 - r.top / innerHeight));
        cards[i].style.transform = 'scale(' + (1 - p * .05) + ')';
        cards[i].style.filter = 'brightness(' + (1 - p * .45) + ')';
      }
    }
    ticking = false;
  });
}
addEventListener('scroll', onScroll, { passive: true });
onScroll();

/* ---------- ПРЕВЬЮ УСЛУГ ---------- */
var preview = document.getElementById('svcPreview');
if(preview){
  var previewImg = preview.querySelector('img');
  if(fine && !rm){
    var px = 0, py = 0, tx = 0, ty = 0, pOn = false;
    document.querySelectorAll('.svc').forEach(function(row){
      row.addEventListener('mouseenter', function(){
        if(previewImg) previewImg.src = row.dataset.img;
        preview.classList.add('on'); pOn = true;
      });
      row.addEventListener('mouseleave', function(){ preview.classList.remove('on'); pOn = false; });
    });
    addEventListener('mousemove', function(e){ tx = e.clientX + 40; ty = e.clientY - 20; });
    (function pl(){
      px += (tx - px) * .12; py += (ty - py) * .12;
      if(pOn || preview.classList.contains('on')){ preview.style.left = px + 'px'; preview.style.top = py + 'px'; }
      requestAnimationFrame(pl);
    })();
  }
}

/* ---------- КУРСОР ---------- */
if(fine && !rm){
  var cursor = document.getElementById('cursor');
  if(cursor){
    var cx = 0, cy = 0, mx = 0, my = 0;
    addEventListener('mousemove', function(e){ mx = e.clientX; my = e.clientY; });
    (function cl(){
      cx += (mx - cx) * .18; cy += (my - cy) * .18;
      cursor.style.transform = 'translate(' + cx + 'px,' + cy + 'px)';
      requestAnimationFrame(cl);
    })();
    document.addEventListener('mouseover', function(e){
      cursor.classList.toggle('is-hover', !!e.target.closest('a, button, .svc, .work-card'));
    });
  }
}

/* ---------- ТАЙМКОД ---------- */
var tcSpan = document.querySelector('#timecode span');
if(tcSpan){
  if(!rm){
    var tStart = performance.now();
    (function tc(now){
      var s = Math.floor((now - tStart) / 1000);
      var mm = String(Math.floor(s / 60)).padStart(2, '0');
      var ss = String(s % 60).padStart(2, '0');
      var ff = String(Math.floor(((now - tStart) % 1000) / 1000 * 24)).padStart(2, '0');
      tcSpan.textContent = 'REC 00:' + mm + ':' + ss + ':' + ff;
      requestAnimationFrame(tc);
    })(tStart);
  }else{
    tcSpan.textContent = 'REC 00:00:00:00';
  }
}

/* ---------- НАВЕРХ ---------- */
var toTop = document.getElementById('toTop');
if(toTop) toTop.addEventListener('click', function(){
  window.scrollTo({ top: 0, behavior: rm ? 'auto' : 'smooth' });
});
})();
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('[data-reveal],.reveal,.in-view').forEach(function(el){el.classList.add('is-in','visible','in-view');});
  document.querySelectorAll('.split').forEach(function(el){el.classList.add('is-in');});
  document.body.classList.add('loaded','js-loaded');
  var loader = document.getElementById('loader');
  if(loader) loader.style.display = 'none';
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getImmersivePortfolioPageHtml(data: ImmersivePortfolioPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'noir'
  return `<!DOCTYPE html>
<html lang="ru" data-theme="${brand}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST® — Иммерсивное портфолио</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/600.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/500-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/600-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jost/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jost/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded/600.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded/800.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/oswald/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/oswald/600.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope/800.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jetbrains-mono/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jetbrains-mono/700.css">
<style>${IMMERSIVE_PORTFOLIO_CSS}</style>
</head>
<body>
${IMMERSIVE_PORTFOLIO_HTML_BODY}
<script>${immersivePortfolioEngineJs(brand, mode)}</script>
</body>
</html>`
}
